export function inferMimeType(url: string): string | null {
    const extension = url.split('.').pop()?.toLowerCase();
    switch (extension) {
        case 'mp3':
            return 'audio/mpeg';
        case 'ogg':
            return 'audio/ogg';
        case 'wav':
            return 'audio/wav';
        default:
            return null;
    }
}

// Force a `.png` file name. The content backend picks the stored blob's
// extension straight off the uploaded file name (uploadFile's
// SupportedFileExtensions), and the editor keys its image handling off that
// extension too — so a PNG named `.jpg` is rejected even though its bytes are
// fine.
function pngFileName(name: string): string {
    if (/\.png$/i.test(name)) return name;
    const stem = name.replace(/\.[^.]+$/, '').trim();
    return `${stem.length > 0 ? stem : 'image'}.png`;
}

// Re-encode an image to PNG at its NATURAL size. Only used for formats the
// pipeline can't store (jpg/webp/…) — never to change an image's dimensions.
async function reencodeAsPng(file: File): Promise<File> {
    const bitmap = await createImageBitmap(file);
    try {
        const canvas = document.createElement('canvas');
        canvas.width = bitmap.width;
        canvas.height = bitmap.height;
        const context = canvas.getContext('2d');
        if (!context) throw new Error('Could not get a 2D canvas context to convert this image to PNG.');
        context.drawImage(bitmap, 0, 0);
        const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/png'));
        if (!blob) throw new Error('Could not encode this image as PNG.');
        return new File([blob], pngFileName(file.name), {type: 'image/png'});
    } finally {
        bitmap.close();
    }
}

// Prepare a user-picked file for upload as an image asset.
//
// Uploaded images are SOURCE assets: their pixels are kept exactly as authored.
// A PNG passes through byte-identical (only its name is normalised if needed);
// anything else is re-encoded to PNG at its natural size, because PNG is the
// only image format the content backend stores and the client loads. Nothing is
// ever resized here — in-game size is resolution-independent (pixelMultiplier is
// derived from the source width, see exportTransform), so a large source costs
// bytes, not correctness. Any downscaling belongs to the export, not the import.
//
// Throws if the file can't be decoded as an image.
export async function prepareImageUpload(file: File): Promise<File> {
    const isPng = file.type === 'image/png' || /\.png$/i.test(file.name);
    if (!isPng) return await reencodeAsPng(file);

    // Already PNG bytes — reuse them untouched, renaming only if the extension
    // doesn't say so (e.g. a `.jpeg` file that is actually a PNG).
    const name = pngFileName(file.name);
    return name === file.name ? file : new File([file], name, {type: 'image/png'});
}
